import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faChevronLeft,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { regionNav, aboutGroups } from "./navdata.js";

/**
 * Mobile drill-down navigation (below 1200px). A panel stack slides between
 * levels: root -> Data by Region -> a region's sub-regions; Tools and About
 * are single flat lists. Back returns one level; the close control resets and
 * dismisses the menu.
 *
 * @param props.onClose Dismiss the whole menu
 * @param props.SearchSlot Optional node rendered at the top of the root view
 * @return JSX element
 */
export function MobileNav({ onClose, SearchSlot }) {
  const [stack, setStack] = useState([{ view: "root" }]);
  const panelRef = useRef(null);
  const current = stack[stack.length - 1];

  const push = (entry) => setStack((s) => [...s, entry]);
  const back = () => setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));

  useEffect(() => {
    panelRef.current?.focus();
  }, [stack.length, current.view]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const titleFor = () => {
    if (current.view === "root") return "HelioData";
    if (current.view === "region") return "Data by Region";
    if (current.view === "about") return "About Us";
    if (current.view === "subregion") return current.region.name;
    return "";
  };

  const region =
    current.view === "subregion" ? current.region : null;

  return (
    <div
      className="mobileNav"
      ref={panelRef}
      tabIndex={-1}
      role="menu"
      aria-label="Site navigation"
    >
      <div className="mobileNavBar">
        <span className="mobileNavTitle">{titleFor()}</span>
        <button
          type="button"
          className="mobileNavClose"
          aria-label="Close menu"
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>

      {current.view !== "root" && (
        <button type="button" className="mobileNavBack" onClick={back}>
          <FontAwesomeIcon icon={faChevronLeft} />
          <span>
            {current.view === "subregion" ? "Back to Data by Region" : "Back"}
          </span>
        </button>
      )}

      {current.view === "root" && (
        <div className="mobileNavList">
          {SearchSlot && <div className="mobileNavSearch">{SearchSlot}</div>}
          <button
            type="button"
            className="mobileNavRow"
            onClick={() => push({ view: "region" })}
          >
            <span>Data by Region</span>
            <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" />
          </button>
          <Link className="mobileNavRow" to="/missionlist" onClick={onClose}>
            <span>Missions &amp; Observatories</span>
          </Link>
          <button
            type="button"
            className="mobileNavRow"
            onClick={() => push({ view: "about" })}
          >
            <span>About Us</span>
            <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" />
          </button>
        </div>
      )}

      {current.view === "region" && (
        <div className="mobileNavList">
          {regionNav.map((r) => (
            <button
              type="button"
              className="mobileNavRow"
              key={r.key}
              onClick={() => push({ view: "subregion", region: r })}
            >
              <span className="mobileNavLead">
                <span className="navRegionIcon navRegionIconSm">
                  <img src={r.icon} alt="" aria-hidden="true" />
                </span>
                {r.name}
              </span>
              <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" />
            </button>
          ))}
        </div>
      )}

      {current.view === "subregion" && region && (
        <div className="mobileNavList">
          <Link
            className="mobileNavRow mobileNavViewAll"
            to={region.landingPath}
            onClick={onClose}
          >
            View all {region.name} data &rarr;
          </Link>
          {region.subRegions.map((sub) => (
            <Link
              className="mobileNavRow"
              key={sub.path}
              to={sub.path}
              onClick={onClose}
            >
              <span>{sub.name}</span>
            </Link>
          ))}
        </div>
      )}

      {current.view === "about" && (
        <div className="mobileNavList">
          {[...aboutGroups.aboutData.links, ...aboutGroups.aboutUs.links].map(
            (item) =>
              item.external ? (
                <a
                  className="mobileNavRow"
                  key={item.link}
                  href={item.link}
                  target="_blank"
                  rel="noreferrer"
                  onClick={onClose}
                >
                  <span className="mobileNavStacked">
                    <span>{item.text}</span>
                    <span className="mobileNavSub">{item.sub}</span>
                  </span>
                </a>
              ) : (
                <Link
                  className="mobileNavRow"
                  key={item.link}
                  to={item.link}
                  onClick={onClose}
                >
                  <span className="mobileNavStacked">
                    <span>{item.text}</span>
                    <span className="mobileNavSub">{item.sub}</span>
                  </span>
                </Link>
              )
          )}
          <a
            className="mobileNavRow"
            href={`mailto:${aboutGroups.contact.email}`}
            onClick={onClose}
          >
            <span className="mobileNavStacked">
              <span>{aboutGroups.contact.title}</span>
              <span className="mobileNavSub">Email the HelioData support team</span>
            </span>
          </a>
        </div>
      )}
    </div>
  );
}
